import _groupBy from 'lodash/groupBy';

import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import getEndOfMonth from 'date-fns/endOfMonth';
import isDate from 'date-fns/isDate';
import getStartOfMonth from 'date-fns/startOfMonth';
import format from 'lib/format';

export const dayErrorState = {
	dayOfWeek: 1,
	date: null,
	label: undefined,
	ariaLabel: undefined,
	ariaHidden: true,
	code: undefined,
};

export const makeDaysInMonth = (firstDayOfMonth, lastDayOfMonth) => {
	const notDates = !isDate(firstDayOfMonth) || !isDate(lastDayOfMonth);
	if (!firstDayOfMonth || !lastDayOfMonth || notDates) {
		return dayErrorState;
	}

	const daysInMonth = eachDayOfInterval({
		start: firstDayOfMonth,
		end: lastDayOfMonth,
	});

	return daysInMonth.map((date) => ({
		date,
		dayOfWeek: date.getDay(),
		label: format(date, 'd'),
		code: format(date, 'yyyy-MM-dd'),
		ariaLabel: format(date, 'EEEE, MMMM do, yyyy'),
		ariaHidden: false,
	}));
};

export const makeMonthForYear = (code, language) => {
	if (typeof code !== 'string') {
		return null;
	}

	const [yearString, monthString] = code.split('-');
	const year = Number(yearString);
	const month = Number(monthString);
	const monthIndex = month - 1;

	if (!year || !month) {
		return null;
	}

	const date = new Date(year, monthIndex);
	const firstDayOfMonth = getStartOfMonth(date);
	const lastDayOfMonth = getEndOfMonth(date);
	const startOfMonth = format(firstDayOfMonth, 'yyyy-MM-dd');
	const endOfMonth = format(lastDayOfMonth, 'yyyy-MM-dd');

	const firstDay = firstDayOfMonth.getDay();
	const lastDay = lastDayOfMonth.getDay();
	const fromEnd = 6 - lastDay;
	const daysInMonth = makeDaysInMonth(firstDayOfMonth, lastDayOfMonth);
	const prepend = Array.from({ length: firstDay }).map((_, dayOfWeek) => ({
		dayOfWeek,
		date: null,
		label: undefined,
		ariaLabel: undefined,
		ariaHidden: true,
		code: undefined,
	}));

	const append = Array.from({ length: fromEnd }).map((_, dayOfWeek) => ({
		dayOfWeek: 6 - dayOfWeek,
		date: null,
		label: undefined,
		ariaLabel: undefined,
		ariaHidden: true,
		code: undefined,
	}));
	const days = [...prepend, ...daysInMonth, ...append];
	const calendar = _groupBy(days, 'dayOfWeek');

	const monthsLabel = date.toLocaleString(language, { month: 'long' }).substring(0, 3);
	const dateRangeLabel = date.toLocaleString(language, { month: 'long', year: 'numeric' })
	return {
		code,
		date,
		year,
		month,
		monthIndex,
		monthsLabel,
		dateRangeLabel,
		startOfMonth,
		endOfMonth,
		daysInMonth,
		calendar,
	};
};

const makeCalendars = (startDateFilter, locale) => {
	const { language } = locale || {};

	const { facets = [] } = startDateFilter;
	const months = facets.map(({ code }) => makeMonthForYear(code, language)).filter(Boolean);
	const dateRangeCalendar = months.sort((a, b) => a.date - b.date);
	const groupedByYear = _groupBy(dateRangeCalendar, 'year');
	const monthsCalendar = Object.keys(groupedByYear).reduce((fullCalendar, year) => {
		const months = groupedByYear[year];
		const fullYearOfMonths = Array.from({ length: 12 }).map((_, i) => {
			let month = months.find((m) => m?.monthIndex === i);

			if (!month) {
				const date = new Date(year, i);
				const monthsLabel = date.toLocaleString(language, { month: 'long' }).substring(0,3);
				month = { monthsLabel, date: '', disabled: true };
			}
			return month;
		});

		fullCalendar[year] = fullYearOfMonths;

		return fullCalendar;
	}, {});
	/**
	 * Do this more clearly
	 * Making here so that we can localize
	 * when we format it in DateRange calendar
	 */
	const grabOne = months[0]?.calendar;
	const daysOfWeek = Object.values(grabOne).map((groupedByDaysOfWeek) => groupedByDaysOfWeek.find((day) => day?.date)?.date);

	return { dateRangeCalendar, monthsCalendar, daysOfWeek };
};

export default makeCalendars;
